// Configuración de la API
// En producción (build), usa rutas relativas (cadena vacía) para que funcione desde cualquier dominio
// En desarrollo, usa la variable de entorno o localhost:3001
const isDev = import.meta.env.DEV;
export const API_BASE_URL = isDev
  ? (import.meta.env.VITE_API_URL || 'http://localhost:3001')
  : (import.meta.env.VITE_API_URL || ''); // Cadena vacía = rutas relativas en producción
export const WS_URL = isDev
  ? (import.meta.env.VITE_WS_URL || 'ws://localhost:3001')
  : (import.meta.env.VITE_WS_URL || `wss://${window.location.host}`);
export const APP_NAME = import.meta.env.VITE_APP_NAME || 'Control de Acceso';

export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/api/auth/login',
  REFRESH: '/api/auth/refresh',
  LOGOUT: '/api/auth/logout',
  ME: '/api/auth/me',
  CHANGE_PASSWORD: '/api/auth/change-password',
  FORGOT_PASSWORD: '/api/auth/forgot-password',
  RESET_PASSWORD: '/api/auth/reset-password',
  CAPTCHA: '/api/auth/captcha',

  // Users
  USERS: '/api/users',
  USER_BY_ID: (id: number) => `/api/users/${id}`,
  USER_ROLES: (id: number) => `/api/users/${id}/roles`,
  USER_QR: (id: number) => `/api/users/${id}/qr`,
  USER_FINGERPRINT: (id: number) => `/api/users/${id}/fingerprint`,
  USER_FACE: (id: number) => `/api/users/${id}/face`,

  // Roles
  ROLES: '/api/roles',
  ROLE_BY_ID: (id: number) => `/api/roles/${id}`,
  ROLE_USERS: (id: number) => `/api/roles/${id}/users`,

  // Organizations
  ORGANIZATION: '/api/organizations',
  ORGANIZATION_CONFIG: '/api/organizations/config',
  ORGANIZATION_STATS: '/api/organizations/stats',
  ORGANIZATION_QUOTA: '/api/organizations/quota',

  // Controllers
  CONTROLLERS: '/api/controllers',
  CONTROLLER_BY_ID: (id: number) => `/api/controllers/${id}`,
  CONTROLLER_HEARTBEAT: (id: number) => `/api/controllers/${id}/heartbeat`,
  CONTROLLER_ACCESS_POINTS: (id: number) => `/api/controllers/${id}/access-points`,

  // Access Points
  ACCESS_POINTS: '/api/access-points',
  ACCESS_POINT_BY_ID: (id: number) => `/api/access-points/${id}`,
  ACCESS_POINT_USERS: (id: number) => `/api/access-points/${id}/users`,
  ACCESS_POINT_LOGS: (id: number) => `/api/access-points/${id}/logs`,
  ACCESS_POINT_ZONES: '/api/access-points/zones',
  ACCESS_POINT_ZONES_BULK_UPDATE: '/api/access-points/zones/bulk-update',

  // Permissions
  PERMISSIONS: '/api/permissions',
  PERMISSIONS_BULK: '/api/permissions/bulk',
  PERMISSION_BY_ID: (id: number) => `/api/permissions/${id}`,
  PERMISSIONS_CHECK: '/api/permissions/check',
  PERMISSIONS_EXPIRING: '/api/permissions/expiring',
  PERMISSIONS_STATS: '/api/permissions/stats',

  // Logs
  LOGS_ACCESS: '/api/logs/access',
  LOGS_AUDIT: '/api/logs/audit',
  LOGS_ACCESS_STATS: '/api/logs/access/stats',
  LOGS_ACCESS_BY_HOUR: '/api/logs/access/by-hour',
  LOGS_TOP_ACCESS_POINTS: '/api/logs/access/top-access-points',
  LOGS_TOP_USERS: '/api/logs/access/top-users',
  LOGS_SYSTEM_ACCESS: '/api/logs/system-access',
  LOGS_SYSTEM_ACCESS_STATS: '/api/logs/system-access/stats',
  LOGS_SYSTEM_ACCESS_EXPORT: '/api/logs/system-access/export',
  LOGS_AUDIT_EXPORT: '/api/logs/audit/export',
  LOGS_AUDIT_STATS: '/api/logs/audit/stats',

  // Dashboard
  DASHBOARD_STATS: '/api/dashboard/stats',
  DASHBOARD_RECENT_EVENTS: '/api/dashboard/recent-events',
  DASHBOARD_ACTIVITY_BY_DAY: '/api/dashboard/activity-by-day',
  DASHBOARD_TOP_ACCESS_POINTS: '/api/dashboard/top-access-points',
  DASHBOARD_ALERTS: '/api/dashboard/alerts',
  DASHBOARD_EMERGENCY_NOTIFY: '/api/dashboard/emergency/notify',
  DASHBOARD_EMERGENCY_DEACTIVATE_NOTIFY: '/api/dashboard/emergency/deactivate-notify',
  DASHBOARD_EMERGENCY_CONFIG: '/api/dashboard/emergency-config',
  DASHBOARD_MULTI_SEDE: '/api/dashboard/multi-sede',
  DASHBOARD_DEPARTMENT: '/api/dashboard/department',

  // Universidad Module
  UNIVERSITY_FACULTIES: '/api/university/faculties',
  UNIVERSITY_FACULTY_BY_ID: (id: number) => `/api/university/faculties/${id}`,
  UNIVERSITY_FACULTY_COURSES: (id: number) => `/api/university/faculties/${id}/courses`,
  UNIVERSITY_FACULTY_STATS: (id: number) => `/api/university/faculties/${id}/stats`,

  UNIVERSITY_COURSES: '/api/university/courses',
  UNIVERSITY_COURSE_BY_ID: (id: number) => `/api/university/courses/${id}`,
  UNIVERSITY_COURSE_ENROLLMENTS: (id: number) => `/api/university/courses/${id}/enrollments`,
  UNIVERSITY_COURSE_ACCESS_POINTS: (id: number) => `/api/university/courses/${id}/access-points`,
  UNIVERSITY_COURSE_OPEN: (id: number) => `/api/university/courses/${id}/open`,
  UNIVERSITY_COURSE_CLOSE: (id: number) => `/api/university/courses/${id}/close`,

  UNIVERSITY_ENROLLMENTS: '/api/university/enrollments',
  UNIVERSITY_ENROLLMENT_BY_ID: (id: number) => `/api/university/enrollments/${id}`,
  UNIVERSITY_ENROLLMENT_COMPLETE: (id: number) => `/api/university/enrollments/${id}/complete`,
  UNIVERSITY_ENROLLMENT_DROP: (id: number) => `/api/university/enrollments/${id}/drop`,
  UNIVERSITY_ENROLLMENT_FAIL: (id: number) => `/api/university/enrollments/${id}/fail`,
  UNIVERSITY_ENROLLMENTS_BY_STUDENT: (studentId: number) => `/api/university/enrollments/student/${studentId}`,

  UNIVERSITY_COURSE_ACCESS: '/api/university/course-access',
  UNIVERSITY_COURSE_ACCESS_BY_ID: (id: number) => `/api/university/course-access/${id}`,
  UNIVERSITY_COURSE_ACCESS_ACTIVATE: (id: number) => `/api/university/course-access/${id}/activate`,
  UNIVERSITY_COURSE_ACCESS_DEACTIVATE: (id: number) => `/api/university/course-access/${id}/deactivate`,
  UNIVERSITY_COURSE_ACCESS_BY_COURSE: (courseId: number) => `/api/university/course-access/course/${courseId}/access-points`,
  UNIVERSITY_COURSE_ACCESS_BY_POINT: (accessPointId: number) => `/api/university/course-access/access-point/${accessPointId}/courses`,

  // Residential Module
  RESIDENTIAL_UNITS: '/api/residential/units',
  RESIDENTIAL_UNIT_BY_ID: (id: number) => `/api/residential/units/${id}`,
  RESIDENTIAL_UNIT_ADD_RESIDENT: (id: number) => `/api/residential/units/${id}/residents`,
  RESIDENTIAL_UNIT_REMOVE_RESIDENT: (id: number, residentId: number) => `/api/residential/units/${id}/residents/${residentId}`,
  RESIDENTIAL_UNIT_VISITORS: (id: number) => `/api/residential/units/${id}/visitors`,
  RESIDENTIAL_UNIT_RESERVATIONS: (id: number) => `/api/residential/units/${id}/reservations`,

  RESIDENTIAL_VISITORS: '/api/residential/visitors',
  RESIDENTIAL_VISITOR_BY_ID: (id: number) => `/api/residential/visitors/${id}`,
  RESIDENTIAL_VISITOR_BY_QR: (qrCode: string) => `/api/residential/visitors/qr/${qrCode}`,
  RESIDENTIAL_VISITOR_ACTIVATE: (id: number) => `/api/residential/visitors/${id}/activate`,
  RESIDENTIAL_VISITOR_SUSPEND: (id: number) => `/api/residential/visitors/${id}/suspend`,
  RESIDENTIAL_VISITOR_VALIDATE: '/api/residential/visitors/validate',
  RESIDENTIAL_VISITOR_ENTRY: (id: number) => `/api/residential/visitors/${id}/entry`,
  RESIDENTIAL_VISITORS_ACTIVE: '/api/residential/visitors/active/list',
  RESIDENTIAL_VISITORS_AUTO_EXPIRE: '/api/residential/visitors/maintenance/auto-expire',
  RESIDENTIAL_VISITORS_CLEANUP: '/api/residential/visitors/maintenance/cleanup',

  RESIDENTIAL_COMMON_AREAS: '/api/residential/common-areas',
  RESIDENTIAL_COMMON_AREA_BY_ID: (id: number) => `/api/residential/common-areas/${id}`,
  RESIDENTIAL_COMMON_AREA_ACTIVATE: (id: number) => `/api/residential/common-areas/${id}/activate`,
  RESIDENTIAL_COMMON_AREA_DEACTIVATE: (id: number) => `/api/residential/common-areas/${id}/deactivate`,
  RESIDENTIAL_COMMON_AREA_CHECK_AVAILABILITY: (id: number) => `/api/residential/common-areas/${id}/check-availability`,
  RESIDENTIAL_COMMON_AREA_RESERVATIONS: (id: number) => `/api/residential/common-areas/${id}/reservations`,
  RESIDENTIAL_COMMON_AREA_STATS: (id: number) => `/api/residential/common-areas/${id}/stats`,
  RESIDENTIAL_COMMON_AREA_IS_OPEN: (id: number) => `/api/residential/common-areas/${id}/is-open`,

  RESIDENTIAL_RESERVATIONS: '/api/residential/reservations',
  RESIDENTIAL_RESERVATION_BY_ID: (id: number) => `/api/residential/reservations/${id}`,
  RESIDENTIAL_RESERVATION_CANCEL: (id: number) => `/api/residential/reservations/${id}/cancel`,
  RESIDENTIAL_RESERVATION_COMPLETE: (id: number) => `/api/residential/reservations/${id}/complete`,
  RESIDENTIAL_RESERVATION_NO_SHOW: (id: number) => `/api/residential/reservations/${id}/no-show`,
  RESIDENTIAL_RESERVATIONS_BY_USER: (userId: number) => `/api/residential/reservations/user/${userId}`,
  RESIDENTIAL_RESERVATIONS_UPCOMING: '/api/residential/reservations/upcoming/list',
  RESIDENTIAL_RESERVATIONS_CHECK_AVAILABILITY: '/api/residential/reservations/check-availability',

  // Corporate Module
  CORPORATE_DEPARTMENTS: '/api/corporate/departments',
  CORPORATE_DEPARTMENTS_DELETED: '/api/corporate/departments/deleted',
  CORPORATE_DEPARTMENT_BY_ID: (id: number) => `/api/corporate/departments/${id}`,
  CORPORATE_DEPARTMENT_EMPLOYEES: (id: number) => `/api/corporate/departments/${id}/employees`,
  CORPORATE_DEPARTMENT_STATS: (id: number) => `/api/corporate/departments/${id}/stats`,
  CORPORATE_DEPARTMENT_RESTORE: (id: number) => `/api/corporate/departments/${id}/restore`,
  CORPORATE_DEPARTMENT_PERMANENT: (id: number) => `/api/corporate/departments/${id}/permanent`,
  CORPORATE_DEPARTMENT_TOGGLE_ACTIVE: (id: number) => `/api/corporate/departments/${id}/toggle-active`,

  // Dispatch (Agenda de Despachos — §3.3 alcance Bodegas)
  DISPATCH_PROVIDERS: '/api/corporate/dispatch/providers',
  DISPATCH_PROVIDER_BY_ID: (id: number) => `/api/corporate/dispatch/providers/${id}`,
  DISPATCH_PROVIDER_LOOKUP_RUT: (rut: string) => `/api/corporate/dispatch/providers/lookup-rut/${encodeURIComponent(rut)}`,
  DISPATCH_SCHEDULES: '/api/corporate/dispatch/schedules',
  DISPATCH_SCHEDULE_BY_ID: (id: number) => `/api/corporate/dispatch/schedules/${id}`,
  DISPATCH_SCHEDULE_ARRIVAL: (id: number) => `/api/corporate/dispatch/schedules/${id}/arrival`,
  DISPATCH_ARRIVALS: '/api/corporate/dispatch/arrivals',
  DISPATCH_ARRIVAL_BY_PLATE: (plate: string) => `/api/corporate/dispatch/arrivals/plate/${plate}`,

  CORPORATE_EMPLOYEES: '/api/corporate/employees',
  CORPORATE_EMPLOYEE_BY_ID: (id: number) => `/api/corporate/employees/${id}`,
  CORPORATE_EMPLOYEE_BY_NUMBER: (employeeNumber: string) => `/api/corporate/employees/number/${employeeNumber}`,
  CORPORATE_EMPLOYEE_SUSPEND: (id: number) => `/api/corporate/employees/${id}/suspend`,
  CORPORATE_EMPLOYEE_REACTIVATE: (id: number) => `/api/corporate/employees/${id}/reactivate`,
  CORPORATE_EMPLOYEE_ON_LEAVE: (id: number) => `/api/corporate/employees/${id}/on-leave`,
  CORPORATE_EMPLOYEE_MARK_DELINQUENT: (id: number) => `/api/corporate/employees/${id}/mark-delinquent`,
  CORPORATE_EMPLOYEE_CLEAR_DELINQUENT: (id: number) => `/api/corporate/employees/${id}/clear-delinquent`,
  CORPORATE_EMPLOYEE_SUBORDINATES: (id: number) => `/api/corporate/employees/${id}/subordinates`,
  CORPORATE_EMPLOYEE_APPOINTMENTS: (id: number) => `/api/corporate/employees/${id}/visitor-appointments`,
  CORPORATE_EMPLOYEE_IS_WORKING: (id: number) => `/api/corporate/employees/${id}/is-working`,
  CORPORATE_EMPLOYEE_TERMINATION_SUMMARY: (id: number) => `/api/corporate/employees/${id}/termination-summary`,
  CORPORATE_EMPLOYEE_TERMINATE_SAFELY: (id: number) => `/api/corporate/employees/${id}/terminate-safely`,

  CORPORATE_MEMBERSHIPS: '/api/corporate/memberships',
  CORPORATE_MEMBERSHIP_BY_ID: (id: number) => `/api/corporate/memberships/${id}`,
  CORPORATE_MEMBERSHIP_BY_USER: (userId: number) => `/api/corporate/memberships/user/${userId}`,
  CORPORATE_MEMBERSHIP_ACTIVE_BY_USER: (userId: number) => `/api/corporate/memberships/user/${userId}/active`,
  CORPORATE_MEMBERSHIP_SUSPEND: (id: number) => `/api/corporate/memberships/${id}/suspend`,
  CORPORATE_MEMBERSHIP_REACTIVATE: (id: number) => `/api/corporate/memberships/${id}/reactivate`,
  CORPORATE_MEMBERSHIP_CANCEL: (id: number) => `/api/corporate/memberships/${id}/cancel`,
  CORPORATE_MEMBERSHIP_RENEW: (id: number) => `/api/corporate/memberships/${id}/renew`,
  CORPORATE_MEMBERSHIPS_EXPIRING: '/api/corporate/memberships/expiring/soon',
  CORPORATE_MEMBERSHIPS_AUTO_EXPIRE: '/api/corporate/memberships/maintenance/auto-expire',
  CORPORATE_MEMBERSHIPS_STATS: '/api/corporate/memberships/stats/general',

  CORPORATE_VISITOR_APPOINTMENTS: '/api/corporate/visitor-appointments',
  CORPORATE_VISITOR_APPOINTMENT_BY_ID: (id: number) => `/api/corporate/visitor-appointments/${id}`,
  CORPORATE_VISITOR_APPOINTMENT_BY_QR: (qrCode: string) => `/api/corporate/visitor-appointments/qr/${qrCode}`,
  CORPORATE_VISITOR_APPOINTMENT_APPROVE: (id: number) => `/api/corporate/visitor-appointments/${id}/approve`,
  CORPORATE_VISITOR_APPOINTMENT_REJECT: (id: number) => `/api/corporate/visitor-appointments/${id}/reject`,
  CORPORATE_VISITOR_APPOINTMENT_CHECK_IN: (id: number) => `/api/corporate/visitor-appointments/${id}/check-in`,
  CORPORATE_VISITOR_APPOINTMENT_CHECK_OUT: (id: number) => `/api/corporate/visitor-appointments/${id}/check-out`,
  CORPORATE_VISITOR_APPOINTMENT_CANCEL: (id: number) => `/api/corporate/visitor-appointments/${id}/cancel`,
  CORPORATE_VISITOR_APPOINTMENT_NO_SHOW: (id: number) => `/api/corporate/visitor-appointments/${id}/no-show`,
  CORPORATE_VISITOR_APPOINTMENTS_PENDING: '/api/corporate/visitor-appointments/pending/approval',
  CORPORATE_VISITOR_APPOINTMENTS_TODAY: '/api/corporate/visitor-appointments/today/list',
  CORPORATE_VISITOR_APPOINTMENTS_CURRENT: '/api/corporate/visitor-appointments/current/visitors',
  CORPORATE_VISITOR_INVITATIONS: '/api/corporate/visitor-appointments/invitations',
  CORPORATE_VISITOR_APPOINTMENT_PROCESS_ACCESS: '/api/corporate/visitor-appointments/process-access',
  CORPORATE_VISITOR_APPOINTMENT_MARK_EXPIRED_NO_SHOW: '/api/corporate/visitor-appointments/mark-expired-no-show',
  CORPORATE_VISITOR_RFID_CARDS: '/api/corporate/visitor-appointments/rfid-cards',
  CORPORATE_VISITOR_RFID_CARD_BY_ID: (id: number) => `/api/corporate/visitor-appointments/rfid-cards/${id}`,
  CORPORATE_VISITOR_RFID_CARD_DISABLE: (id: number) => `/api/corporate/visitor-appointments/rfid-cards/${id}/disable`,
  CORPORATE_VISITOR_RFID_CARD_ENABLE: (id: number) => `/api/corporate/visitor-appointments/rfid-cards/${id}/enable`,

  // Sedes (Locations)
  CORPORATE_SEDES: '/api/corporate/sedes',
  CORPORATE_SEDE_BY_ID: (id: number) => `/api/corporate/sedes/${id}`,
  CORPORATE_SEDE_TOGGLE_ACTIVE: (id: number) => `/api/corporate/sedes/${id}/toggle-active`,
  CORPORATE_SEDES_STATS: '/api/corporate/sedes/stats',
  CORPORATE_SEDES_STATS_BY_COUNTRY: '/api/corporate/sedes/stats/by-country',
  CORPORATE_SEDES_TIMEZONES: '/api/corporate/sedes/timezones',
  CORPORATE_SEDES_WITH_GEOLOCATION: '/api/corporate/sedes/with-geolocation',
  CORPORATE_SEDE_CHECK_HOLIDAY: (id: number) => `/api/corporate/sedes/${id}/check-holiday`,
  CORPORATE_SEDE_VALIDATE_GEOFENCE: (id: number) => `/api/corporate/sedes/${id}/validate-geofence`,

  // Bulk Import (Carga Masiva)
  CORPORATE_BULK_IMPORT_TEMPLATE: '/api/corporate/bulk-import/template',
  CORPORATE_BULK_IMPORT_DEPARTMENTS: '/api/corporate/bulk-import/departments',
  CORPORATE_BULK_IMPORT_DEPARTMENTS_TEMPLATE: '/api/corporate/bulk-import/departments-template',
  CORPORATE_BULK_IMPORT_EMPLOYEES: '/api/corporate/bulk-import/employees',

  // Reports (Reportes de Trazabilidad)
  CORPORATE_REPORTS_DAILY_ACCESS: '/api/corporate/reports/daily-access',
  CORPORATE_REPORTS_DAILY_ACCESS_EXPORT: '/api/corporate/reports/daily-access/export',
  CORPORATE_REPORTS_DENIED_ACCESS: '/api/corporate/reports/denied-access',
  CORPORATE_REPORTS_DENIED_ACCESS_EXPORT: '/api/corporate/reports/denied-access/export',
  CORPORATE_REPORTS_USER_TRACE: '/api/corporate/reports/user-trace',
  CORPORATE_REPORTS_USER_TRACE_EXPORT: '/api/corporate/reports/user-trace/export',
  CORPORATE_REPORTS_ZONE_USAGE: '/api/corporate/reports/zone-usage',
  CORPORATE_REPORTS_ZONE_USAGE_EXPORT: '/api/corporate/reports/zone-usage/export',
  CORPORATE_REPORTS_EMPLOYEES_LIST: '/api/corporate/reports/employees-list',

  // Parking (Estacionamientos)
  CORPORATE_PARKING_SPOTS:  '/api/corporate/parking/spots',
  CORPORATE_PARKING_CONFIG: '/api/corporate/parking/config',
  CORPORATE_PARKING_STATS:  '/api/corporate/parking/stats',
  CORPORATE_PARKING_SPOT_BY_ID: (id: number) => `/api/corporate/parking/spots/${id}`,
  CORPORATE_PARKING_AUTO_ASSIGN: '/api/corporate/parking/auto-assign',

  // End Users Export
  END_USERS_EXPORT_EXCEL: '/api/end-users/export/excel',
};

export const TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const USER_KEY = 'user';
export const PERMISSIONS_KEY = 'permissions';
